import React from 'react';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Factory, TrendingUp, Target, CheckCircle, BarChart3, Users, Lightbulb, Award, Calendar, Building } from 'lucide-react';

const QRMJourney = () => {
  const journeyTimeline = [
    {
      year: "2016",
      title: "QRM Discovery",
      description: "First encounter with QRM methodology and decision to pursue deep learning",
      milestone: "Beginning of 10-year journey"
    },
    {
      year: "2017-2019", 
      title: "Foundation Building",
      description: "QRM Alpha and Silver training, building fundamental understanding",
      milestone: "Core competency development"
    },
    {
      year: "2020-2022",
      title: "Advanced Mastery",
      description: "QRM Gold training and advanced concepts, deepening expertise",
      milestone: "Expert-level knowledge"
    },
    {
      year: "2023-2026",
      title: "Practical Application & Recognition",
      description: "Real-world implementation experience, mentor collaboration, and industry recognition",
      milestone: "Professional mastery & LEAN LEAD Winner 2026 🏆",
      award: "Best QRM Practice Award - Belgium"
    },
    {
      year: "2026",
      title: "Smart QRM Navigator",
      description: "Creating the world's most comprehensive QRM business case platform",
      milestone: "Knowledge sharing"
    }
  ];

  const mentors = [
    {
      id: 1,
      name: "Professor Rajan Suri",
      title: "Creator & Founder of QRM",
      company: "University of Wisconsin-Madison",
      linkedinUrl: "https://www.linkedin.com/in/rajan-suri-8952901/",
      photoUrl: "https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/s1w338mw_RAJAN%20SURI%20and%20janpauwels.JPG",
      role: "The master teacher who created QRM methodology. Taught me the foundational principles of time-based thinking, MCT analysis, and the complete QRM philosophy during masterclasses and through his seminal work 'It's About Time'.",
      testimonial: "Another award to a company for QRM implementation! I am pleased to share that another company in Belgium has received an award for their success with QRM. Congratulations to Jan Pauwels who led these efforts and to his whole team who helped to achieve the exceptional results, and also to Pascal Pollet who supported the QRM training and coaching for the implementation efforts.",
      yearsWorked: "2015-Present",
      significance: "QRM Creator & Master Teacher",
      photoCaption: "Jan with Professor Rajan Suri holding 'It's About Time' book"
    },
    {
      id: 2,
      name: "Pascal Pollet",
      title: "QRM Expert & Senior Consultant",
      company: "Sirris, Belgium",
      linkedinUrl: "https://www.linkedin.com/in/pascal-pollet/",
      photoUrl: "https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/fb5c7aab_jan%20pauwels%20and%20pascal%20pollet.JPG",
      role: "My implementation guide who provided hands-on QRM expertise throughout the transformation. Helped translate QRM theory into practical metal construction applications and guided the organizational change management.",
      testimonial: "Jan Pauwels is an experienced business leader who thoroughly transformed his own company through the application of Quick Responsive Manufacturing principles. Thanks to this transformation, his company grew into a healthy and thriving organization. Jan knows like no other how theory must be translated into daily reality, and combines strategic insight with extensive practical knowledge.",
      yearsWorked: "2015-2022",
      significance: "Implementation Guide & QRM Practitioner",
      photoCaption: "Jan with Pascal Pollet - QRM transformation partnership"
    },
    {
      id: 3,
      name: "Gino Quensis",
      title: "QRM Expert & Lean Lead Authority",
      company: "Lean Lead",
      linkedinUrl: "https://www.linkedin.com/in/gino-quensis/",
      photoUrl: "https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/w1k1zzlz_Gino%20Quensis%20and%20Jan%20Pauwels.JPG",
      role: "Advanced QRM mentor who helped me understand the integration of Lean and QRM methodologies, leadership aspects of transformation, and the broader continuous improvement landscape in manufacturing.",
      testimonial: "Under the leadership of Jan Pauwels, a Belgian sheet metal manufacturer has shown what is possible when QRM competence, passion and determination come together. Jan was able to inspire his team to rethink processes, build work cells and Q-ROC teams and thus drastically reduce lead times. His focus on customer orientation and continuous improvement has made the company more agile and future-proof. Thanks to Jan's vision and determination, the company was crowned with the prestigious Lean Lead Award. He is an inspiring example for everyone who wants to make real impact with QRM.",
      yearsWorked: "2018-Present",
      significance: "Advanced QRM & Lean Integration Expert",
      photoCaption: "Jan receiving LEAN LEAD Award 2026 from Gino Quensis - QRM Best Practice recognition"
    },
    {
      id: 4,
      name: "Thomas Luiten",
      title: "Plant Manager & European QRM Pioneer",
      company: "Interfocus, Holland",
      linkedinUrl: "https://www.linkedin.com/in/thomas-luiten/",
      photoUrl: "https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/zeicerel_jan%20pauwels%20and%20thomas%20Luiten%20-%20pioneeer%20of%20qrm%20in%20Europe.JPG",
      role: "An amazing honor to learn from the European QRM pioneer who achieved the remarkable feat of lot size 1 production for chimneys. Through factory visits and open discussions, Thomas inspired me with his innovative QRM applications and demonstrated what's truly possible with visionary implementation.",
      testimonial: "Luiten started to reform the company to a process oriented organization, by creating multidisciplinary teams. 'For each group of products we composed a team, with sellers, engineers and production employees. Each team is responsible for the entire process from sales to delivery.' We have one workcell for each product group, which deals with the complete process from order to delivery. QRM-tools are used to accelerate processes, like applying Value Stream Maps to find and eliminate non-value adding activities. Within Interfocos there are no longer departments, which strongly reduces the risk of local optimization. A change is seen as profitable if it leads to an overall reduction in lead time. Thanks to these improvements, the delivery time of the stoves has already been cut in half.",
      yearsWorked: "Inspirational Factory Visit & Ongoing Influence",
      significance: "European QRM Pioneer - Lot Size 1 Visionary",
      photoCaption: "Jan with Thomas Luiten - European QRM pioneer and inspirational leader",
      mentorType: "inspirator"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <PageSEO path="/qrm-journey" />
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Jan Pauwels — My QRM Journey & Mentors</h1>
            <p className="text-xl text-gray-600 mb-2">
              10 Years of QRM Learning, Growth, and Gratitude (2016-2026)
            </p>
            <p className="text-lg text-gray-500">
              The personal journey and expert mentorship behind the Smart QRM Navigator
            </p>
          </div>

          {/* Founder Introduction */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Users className="mr-3 h-6 w-6 text-blue-600" />
                  About the Founder
                </h2>
                <div className="space-y-4 text-gray-700">
                  <p className="leading-relaxed">
                    <strong>Jan Pauwels</strong> - QRM Expert, Consultant, and Founder/CEO of Smart QRM Navigator. 
                    The driving force behind one of Europe's most celebrated QRM transformations.
                  </p>
                  <p className="leading-relaxed">
                    <strong>Success Story:</strong> Led a Belgian sheet metal manufacturer from near bankruptcy to significant revenue growth and improved EBITDA margins through QRM implementation. 
                    Achieved dramatic lead time reduction and high delivery reliability - earning the 2026 LEAN LEAD WINNER award for Best QRM Practice.
                  </p>
                  <p className="leading-relaxed">
                    Since 2016, I have been privileged to learn from exceptional mentors including Professor Rajan Suri (QRM creator), 
                    Pascal Pollet (Sirris), and Gino Quensis (Lean Lead). This 10-year journey of learning and real-world application 
                    led to creating the Smart QRM Navigator.
                  </p>
                  <p className="leading-relaxed">
                    <strong>Mission:</strong> Share authentic QRM knowledge and provide manufacturing executives with tools to make 
                    confident investment decisions based on proven methodology and real-world results.
                  </p>
                  
                  {/* Professional Links */}
                  <div className="flex space-x-4 mt-6">
                    <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                      <a 
                        href="https://www.linkedin.com/in/jan-pauwels-8b320b24"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-4 py-2 border border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-md text-sm font-medium transition-colors"
                      >
                        LinkedIn Profile
                      </a>
                      <span className="text-xs text-gray-500">
                        (copy this <strong className="text-blue-600 select-all">linkedin.com/in/jan-pauwels-8b320b24</strong> in your browser)
                      </span>
                    </div>
                    <Button 
                      variant="outline"
                      className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                      onClick={() => window.open('mailto:info@smartqrmnavigator.com')}
                    >
                      Contact: info@smartqrmnavigator.com
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Founder Photo */}
              <div className="text-center">
                <div className="w-48 h-48 mx-auto rounded-full overflow-hidden border-4 border-blue-200 shadow-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_qrm-navigator/artifacts/6ul4nujz_jan%20pauwels.JPG"
                    alt="Jan Pauwels - QRM Expert & Navigator Founder"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-600 font-medium mt-3">
                  Jan Pauwels<br />
                  QRM Expert & Consultant<br />
                  Smart QRM Navigator Founder<br />
                  AI Generalist - AI Catalyst
                </p>
                <p className="text-xs text-gray-500 mt-2 italic max-w-xs mx-auto">
                  "Holding an MSc in Industrial Engineering with a core specialization in Automation from KU Leuven, I bridge the gap between complex technical systems and streamlined manufacturing operations."
                </p>
              </div>
            </div>
          </Card>

          {/* Journey Timeline */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
              <Calendar className="mr-3 h-6 w-6 text-gray-600" />
              QRM Learning Journey (2016-2026)
            </h2>
            
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-blue-200"></div>
              
              <div className="space-y-8">
                {journeyTimeline.map((period, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm mr-6 relative z-10">
                      {period.year}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{period.title}</h3>
                      <p className="text-gray-600 mb-2">{period.description}</p>
                      <Badge className="bg-blue-100 text-blue-800">{period.milestone}</Badge>
                      {period.award && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <div className="text-yellow-600">🏆</div>
                            <div>
                              <p className="font-bold text-yellow-800 text-sm">LEAN LEAD Winner 2026</p>
                              <p className="text-yellow-700 text-xs">{period.award}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Mentors Section */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Award className="mr-3 h-6 w-6 text-green-600" />
              My QRM Mentors & Inspirational Pioneers
            </h2>
            <p className="text-gray-700 mb-8 leading-relaxed">
              I have been incredibly fortunate to work closely with three exceptional QRM mentors and have the honor 
              to learn from a true European QRM pioneer. Their wisdom, guidance, and inspiration shaped my understanding 
              and made this knowledge platform possible.
            </p>
            
            <div className="space-y-12">
              {mentors.map((mentor, index) => (
                <Card key={mentor.id} className="p-8 bg-white hover:shadow-xl transition-all duration-300">
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
                    
                    {/* Mentor Photo */}
                    <div className="text-center">
                      <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-gray-300 shadow-lg">
                        <img 
                          src={mentor.photoUrl}
                          alt={`${mentor.name} with Jan Pauwels`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {mentor.photoCaption && (
                        <p className="text-xs text-gray-500 mt-2 italic">{mentor.photoCaption}</p>
                      )}
                      <a 
                        href={mentor.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center mt-4 px-3 py-1.5 border border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-md text-sm font-medium transition-colors"
                      >
                        LinkedIn Profile
                      </a>
                    </div>

                    {/* Mentor Details */}
                    <div className="lg:col-span-3">
                      <div className="mb-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{mentor.name}</h3>
                        <p className="text-lg text-blue-600 font-medium mb-1">{mentor.title}</p>
                        <p className="text-gray-600 mb-3">{mentor.company}</p>
                        <div className="flex space-x-2">
                          <Badge className="bg-green-100 text-green-800">{mentor.yearsWorked}</Badge>
                          <Badge className={`${
                            mentor.mentorType === 'inspirator' 
                              ? 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 border-yellow-300 font-bold' 
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {mentor.significance}
                          </Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Role & Contribution */}
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Mentorship Role:</h4>
                          <p className="text-gray-700 leading-relaxed text-sm">{mentor.role}</p>
                        </div>

                        {/* Testimonial */}
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">
                            {mentor.mentorType === 'inspirator' ? 'How He Inspired Me:' : 'Professional Reference:'}
                          </h4>
                          <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                            <p className="text-blue-800 italic text-sm leading-relaxed">"{mentor.testimonial}"</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>

          {/* Gratitude Statement */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Lightbulb className="mr-3 h-6 w-6 text-yellow-600" />
              A Message of Gratitude
            </h2>
            <div className="text-gray-700 space-y-4">
              <p className="leading-relaxed">
                This Smart QRM Navigator platform represents 10 years of learning, growth, and collaboration. 
                None of this would have been possible without the exceptional guidance and mentorship I received.
              </p>
              <p className="leading-relaxed">
                <strong>To my four mentors:</strong> Your patience, wisdom, and dedication to sharing QRM knowledge has not only 
                shaped my professional journey but has enabled me to create this resource for the entire manufacturing community. 
                Your influence extends far beyond what you might realize.
              </p>
              <p className="leading-relaxed">
                <strong>Special thanks to my partner Mady</strong> for her patience and moral support throughout all those many hours 
                of working and learning. This journey required countless evenings and weekends dedicated to mastering QRM methodology, 
                and her understanding and encouragement made it all possible.
              </p>
              <p className="leading-relaxed">
                <strong>Special thanks to the great team</strong> who participated in the same journey. The QRM transformation 
                was only possible because of the dedication, openness to change, and collaborative spirit of our entire team. 
                From operators learning new skills through cross-training to engineers embracing new workflows, every team member 
                contributed to our success. Their willingness to embrace the QRM mindset and work together made the difference 
                between theory and reality.
              </p>
              <p className="leading-relaxed">
                <strong>To the QRM community:</strong> This platform is my way of paying forward the incredible knowledge and 
                support I received. My hope is that others can benefit from authentic QRM methodology and avoid the common 
                pitfalls in implementation.
              </p>
              <p className="leading-relaxed font-medium text-gray-900">
                "The best way to honor great teachers is to become a great teacher yourself." - This platform is my attempt 
                to share what I've learned and help others succeed with QRM.
              </p>
            </div>
          </Card>

          {/* Platform Connection */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <TrendingUp className="mr-3 h-6 w-6 text-purple-600" />
              How This Journey Built the Smart QRM Navigator
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Knowledge Integration</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Authentic Methodology:</strong> Rajan Suri's original formulas and principles</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Complete Training:</strong> Alpha → Silver → Gold progression integrated</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Real Experience:</strong> Practical case study and implementation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Industry Expertise:</strong> Metalworking-specific knowledge and insights</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Platform Features Born from Experience</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>EBITDA Focus:</strong> Addressing the #1 shareholder question</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Sector Intelligence:</strong> 20+ business types with tailored advice</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Comprehensive Vocabulary:</strong> 165+ expert terms from all training levels</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Real Case Studies:</strong> BMW, NIKO and Belgian manufacturers with actual results</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Press & Media Coverage */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Users className="mr-3 h-6 w-6 text-indigo-600" />
              Industry Recognition
            </h2>
            <p className="text-gray-700 mb-8 leading-relaxed">
              Jan Pauwels' QRM expertise and transformation leadership have been recognized through industry awards 
              and manufacturing excellence programs. His real-world application of QRM methodology has achieved 
              exceptional results for Belgian manufacturers.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Recognition Summary */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Key Achievements</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* LEAN LEAD Award */}
                  <div className="p-4 bg-white rounded-lg border border-yellow-200 hover:shadow-lg transition-all">
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center text-white font-bold mx-auto mb-2">
                        🏆
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1">LEAN LEAD Award Winner</h4>
                      <p className="text-xs text-gray-600 mb-2">2026 Best QRM Practice</p>
                    </div>
                    <div className="text-center mb-3">
                      <Badge className="bg-yellow-100 text-yellow-800 text-xs mr-1">Award Winner</Badge>
                      <Badge className="bg-green-100 text-green-800 text-xs">QRM Excellence</Badge>
                    </div>
                  </div>

                  {/* QRM Certification */}
                  <div className="p-4 bg-white rounded-lg border border-green-200 hover:shadow-lg transition-all">
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold mx-auto mb-2">
                        📜
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1">QRM Certified Expert</h4>
                      <p className="text-xs text-gray-600 mb-2">Alpha → Silver → Gold</p>
                    </div>
                    <div className="text-center mb-3">
                      <Badge className="bg-green-100 text-green-800 text-xs mr-1">Gold Level</Badge>
                      <Badge className="bg-blue-100 text-blue-800 text-xs">10+ Years</Badge>
                    </div>
                  </div>

                  {/* Industry Recognition */}
                  <div className="p-4 bg-white rounded-lg border border-blue-200 hover:shadow-lg transition-all">
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold mx-auto mb-2">
                        🌍
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1">European Recognition</h4>
                      <p className="text-xs text-gray-600 mb-2">Industry Publications Featured</p>
                    </div>
                    <div className="text-center mb-3">
                      <Badge className="bg-blue-100 text-blue-800 text-xs mr-1">Featured</Badge>
                      <Badge className="bg-purple-100 text-purple-800 text-xs">Europe</Badge>
                    </div>
                  </div>

                  {/* Transformation Success */}
                  <div className="p-4 bg-white rounded-lg border border-purple-200 hover:shadow-lg transition-all">
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center text-white font-bold mx-auto mb-2">
                        📈
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1">Transformation Success</h4>
                      <p className="text-xs text-gray-600 mb-2">Belgian Manufacturer</p>
                    </div>
                    <div className="text-center mb-3">
                      <Badge className="bg-purple-100 text-purple-800 text-xs mr-1">Lead Time</Badge>
                      <Badge className="bg-green-100 text-green-800 text-xs">EBITDA</Badge>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Recognition Impact Stats */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Professional Recognition</h3>
                
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Industry Publications:</span>
                      <span className="font-bold text-indigo-600">Multiple Features</span>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">QRM Case Studies:</span>
                      <span className="font-bold text-green-600">Official Recognition</span>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Awards Coverage:</span>
                      <span className="font-bold text-yellow-600">LEAN LEAD 2026</span>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Expert Recognition:</span>
                      <span className="font-bold text-purple-600">QRM Authority Status</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <h4 className="font-semibold text-indigo-800 mb-2">Professional Impact</h4>
                  <p className="text-indigo-700 text-sm leading-relaxed">
                    Jan's expertise and transformation results have attracted attention from manufacturing 
                    publications and QRM institutions, establishing him as a recognized authority in 
                    Quick Responsive Manufacturing implementation.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Professional Credentials */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Award className="mr-3 h-6 w-6 text-gray-600" />
              QRM Credentials & Experience
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-3xl font-bold text-blue-600 mb-2">10</div>
                <div className="text-sm text-gray-600">Years QRM Journey</div>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <div className="text-3xl font-bold text-green-600 mb-2">3</div>
                <div className="text-sm text-gray-600">Expert Mentors</div>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-xl">
                <div className="text-3xl font-bold text-purple-600 mb-2">1</div>
                <div className="text-sm text-gray-600">Inspirational Pioneer</div>
              </div>
              <div className="text-center p-6 bg-yellow-50 rounded-xl">
                <div className="text-3xl font-bold text-yellow-600 mb-2">165+</div>
                <div className="text-sm text-gray-600">QRM Terms Mastered</div>
              </div>
            </div>
          </Card>

          {/* Call to Action */}
          <Card className="p-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Experience the Knowledge Journey Result</h2>
            <p className="text-blue-100 mb-6 text-lg">
              Explore the Smart QRM Navigator - built from 10 years of learning, mentorship, and real-world application
            </p>
            <div className="space-x-4">
              <Button 
                size="lg" 
                className="px-8 py-3 bg-white text-blue-600 hover:bg-blue-50 font-semibold"
                onClick={() => window.location.href = '/business-case'}
              >
                <BarChart3 className="mr-2 h-5 w-5" />
                Try the EBITDA Calculator
              </Button>
              <Button 
                variant="outline"
                size="lg" 
                className="px-8 py-3 border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold"
                onClick={() => window.location.href = '/success-cases'}
              >
                <Award className="mr-2 h-5 w-5" />
                View Success Stories
              </Button>
            </div>
          </Card>

          {/* Navigation */}
          <div className="text-center mt-12">
            <div className="space-x-4">
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/qrm-education'}
                className="px-8 py-3"
              >
                ← QRM Education
              </Button>
              <Button 
                onClick={() => window.location.href = '/'}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700"
              >
                Back to Home
              </Button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default QRMJourney;
